import type { Data, Op } from './types'

export const DEL_KEY = '__del'

export function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (v === null || typeof v !== 'object') return false
  const proto = Object.getPrototypeOf(v)
  return proto === Object.prototype || proto === null
}

// JSON semantics: undefined dropped from objects, null in arrays, Date -> ISO string.
export function clone<T>(v: T): T {
  return cloneAny(v) as T
}

function cloneAny(v: unknown): unknown {
  if (v === null || typeof v !== 'object') {
    if (typeof v === 'number' && !Number.isFinite(v)) return null
    return v
  }
  if (Array.isArray(v)) return v.map((x) => (x === undefined ? null : cloneAny(x)))
  if (v instanceof Date) return v.toISOString()
  const out: Record<string, unknown> = {}
  for (const k of Object.keys(v)) {
    const x = (v as Record<string, unknown>)[k]
    if (x === undefined || typeof x === 'function') continue
    out[k] = cloneAny(x)
  }
  return out
}

export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false
  if (Array.isArray(a) !== Array.isArray(b)) return false
  if (Array.isArray(a)) {
    const bb = b as unknown[]
    if (a.length !== bb.length) return false
    for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], bb[i])) return false
    return true
  }
  const ao = a as Record<string, unknown>
  const bo = b as Record<string, unknown>
  const ak = Object.keys(ao)
  if (ak.length !== Object.keys(bo).length) return false
  for (const k of ak) {
    if (!Object.prototype.hasOwnProperty.call(bo, k)) return false
    if (!deepEqual(ao[k], bo[k])) return false
  }
  return true
}

export function isDel(v: unknown): boolean {
  return isPlainObject(v) && v[DEL_KEY] === true && Object.keys(v).length === 1
}

// The one definition of what an op does to a doc; the server applies the same rules.
export function applyOp(base: Data | null, op: Pick<Op, 'mode' | 'data'>): Data | null {
  if (op.mode === 'delete') return null
  const data = op.data ?? {}
  if (op.mode === 'set') return clone(data)
  const out: Data = base ? { ...base } : {}
  for (const k of Object.keys(data)) {
    if (isDel(data[k])) delete out[k]
    else out[k] = clone(data[k])
  }
  return out
}

export function splitPath(path: string): { parent: string; collection: string; id: string } {
  const segs = path.split('/')
  return {
    parent: segs.slice(0, -2).join('/'),
    collection: segs[segs.length - 2] ?? '',
    id: segs[segs.length - 1] ?? '',
  }
}
